export type InterviewScreenShareIssue =
  | 'not_entire_screen'
  | 'no_system_audio'
  | 'unknown_surface'
  | 'silent_system_audio'

export type ScreenCaptureFailureReason = InterviewScreenShareIssue | 'cancel' | 'generic'

export class InterviewScreenCaptureError extends Error {
  readonly reason: ScreenCaptureFailureReason

  constructor(message: string, reason: ScreenCaptureFailureReason) {
    super(message)
    this.name = 'InterviewScreenCaptureError'
    this.reason = reason
  }
}

export function isInterviewScreenCaptureError(err: unknown): err is InterviewScreenCaptureError {
  return err instanceof InterviewScreenCaptureError
}

export interface InterviewScreenShareValidation {
  ok: boolean
  issue?: InterviewScreenShareIssue
}

export function normalizeInterviewSessionStatus(status: string | undefined): string {
  return (status ?? '').trim().toLowerCase().replace(/\s+/g, '_')
}

export function isInterviewConductTerminalStatus(status: string | undefined): boolean {
  const s = normalizeInterviewSessionStatus(status)
  return ['completed', 'cancelled', 'canceled', 'failed'].includes(s)
}

export function conductShouldRequireScreenShare(status: string | undefined): boolean {
  return !!status && !isInterviewConductTerminalStatus(status)
}

export function isDisplayCaptureAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  )
}

export function getInterviewDisplayMediaConstraints(): DisplayMediaStreamOptions {
  return {
    video: {
      displaySurface: 'monitor',
      cursor: 'always',
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    audio: true,
    // Asks the browser to pre-select system audio in its share dialog, so the
    // candidate does not have to notice and tick it themselves. It is a HINT,
    // not a guarantee: Chromium honours it, other browsers ignore it, and the
    // candidate can still untick the box. The audible-audio validation
    // elsewhere in this file is what actually enforces it.
    systemAudio: 'include',
    preferCurrentTab: false,
    selfBrowserSurface: 'exclude',
  } as DisplayMediaStreamOptions
}

const FALLBACK_CONSTRAINTS: DisplayMediaStreamOptions = {
  video: {
    displaySurface: 'monitor',
    cursor: 'always',
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
  audio: true,
  systemAudio: 'include',
} as DisplayMediaStreamOptions

const MINIMAL_CONSTRAINTS = { video: true, audio: true } as DisplayMediaStreamOptions

export function isScreenShareCancelledError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const name = (err as DOMException).name
  return name === 'NotAllowedError' || name === 'AbortError'
}

export async function requestInterviewDisplayMedia(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getDisplayMedia(getInterviewDisplayMediaConstraints())
  } catch (err) {
    if (isScreenShareCancelledError(err)) throw err
  }

  try {
    return await navigator.mediaDevices.getDisplayMedia(FALLBACK_CONSTRAINTS)
  } catch (err) {
    if (isScreenShareCancelledError(err)) throw err
  }

  return navigator.mediaDevices.getDisplayMedia(MINIMAL_CONSTRAINTS)
}

export function interviewShareIssueMessage(issue?: InterviewScreenShareIssue): string {
  switch (issue) {
    case 'not_entire_screen':
      return 'Select the Entire screen tab (not Window or Chrome tab), enable Also share system audio, then click Share.'
    case 'no_system_audio':
      return 'Turn on Also share system audio in the share dialog, then click Share.'
    case 'unknown_surface':
      return 'Entire screen with system audio could not be verified. Use Entire screen + system audio and try again.'
    case 'silent_system_audio':
      return "We can't hear the candidate yet. Ask them to say something out loud — this will resolve automatically once we detect their voice."
    default:
      return 'You must share your entire screen with system audio to record this interview.'
  }
}

function normalizeDisplaySurface(raw: string | undefined): string | undefined {
  if (raw == null || String(raw).trim() === '') return undefined
  return String(raw).trim().toLowerCase()
}

function readVideoDisplaySurface(track: MediaStreamTrack): string | undefined {
  const settings = track.getSettings() as MediaTrackSettings & { displaySurface?: string }
  if (settings.displaySurface) return normalizeDisplaySurface(settings.displaySurface)

  const caps = typeof track.getCapabilities === 'function' ? track.getCapabilities() : {}
  const anyCaps = caps as { displaySurface?: string | string[] }
  if (typeof anyCaps.displaySurface === 'string') {
    return normalizeDisplaySurface(anyCaps.displaySurface)
  }
  if (Array.isArray(anyCaps.displaySurface) && anyCaps.displaySurface.length === 1) {
    return normalizeDisplaySurface(anyCaps.displaySurface[0])
  }
  return undefined
}

function reportsDisplaySurfaceCapability(track: MediaStreamTrack): boolean {
  try {
    const caps = track.getCapabilities?.() as { displaySurface?: string[] } | undefined
    return Array.isArray(caps?.displaySurface) && caps.displaySurface.length > 0
  } catch {
    return false
  }
}

function isLikelyCameraTrack(track: MediaStreamTrack): boolean {
  if (track.kind !== 'video') return true
  const s = track.getSettings() as MediaTrackSettings & { facingMode?: string }
  const fm = s.facingMode
  return fm === 'user' || fm === 'environment'
}

function hasLiveVideo(track: MediaStreamTrack | undefined): boolean {
  return !!track && track.kind === 'video' && track.readyState === 'live'
}

export async function waitForVideoDisplaySurface(
  track: MediaStreamTrack,
  opts?: { attempts?: number; intervalMs?: number }
): Promise<string | undefined> {
  const attempts = opts?.attempts ?? 20
  const intervalMs = opts?.intervalMs ?? 50

  for (let i = 0; i < attempts; i++) {
    const surface = readVideoDisplaySurface(track)
    if (surface) return surface
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  return readVideoDisplaySurface(track)
}

function hasLiveUnmutedAudio(stream: MediaStream): boolean {
  return stream.getAudioTracks().some(
    (t) => t.readyState === 'live' && t.enabled && !t.muted
  )
}

// A shared audio track (system audio or mic) can be reported as
// live/enabled/unmuted by the browser while carrying no actual sound — e.g.
// the call's audio is playing through an output device other than the one
// the OS is capturing for the share, or the wrong mic is selected.
// hasLiveUnmutedAudio() only sees track state flags, never the signal
// itself, so it cannot catch this. This samples real amplitude, returning
// true as soon as a signal appears (rather than always waiting the full
// window out) so a genuinely-live source resolves quickly.
const DEFAULT_AUDIO_SAMPLE_MS = 500
const AUDIO_SAMPLE_POLL_MS = 50
// Byte time-domain data centers on 128 for silence; this is a low bar meant
// only to catch true silence, not to flag a quiet-but-present source.
const AUDIO_SAMPLE_THRESHOLD = 4

export async function hasAudibleAudioSignal(
  stream: MediaStream,
  opts?: { timeoutMs?: number }
): Promise<boolean> {
  if (stream.getAudioTracks().length === 0) return false
  const timeoutMs = opts?.timeoutMs ?? DEFAULT_AUDIO_SAMPLE_MS

  let audioContext: AudioContext | null = null
  try {
    audioContext = new AudioContext()
    const source = audioContext.createMediaStreamSource(stream)
    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 2048
    source.connect(analyser)

    const data = new Uint8Array(analyser.fftSize)
    const start = Date.now()

    while (Date.now() - start < timeoutMs) {
      analyser.getByteTimeDomainData(data)
      for (let i = 0; i < data.length; i++) {
        if (Math.abs(data[i] - 128) > AUDIO_SAMPLE_THRESHOLD) return true
      }
      await new Promise((r) => setTimeout(r, AUDIO_SAMPLE_POLL_MS))
    }

    return false
  } catch {
    // If sampling itself fails (e.g. unsupported API), don't block the
    // interview on this extra check — fall back to allowing it through.
    return true
  } finally {
    audioContext?.close()
  }
}

// Structural checks only (display surface + the share-dialog's own audio
// flag) — deliberately does NOT judge whether real sound is present. That
// call requires a longer, retriable wait and, on timeout, keeping the
// stream alive for background recovery instead of a hard failure — handled
// by the caller (useMediaRecorder.ts) via hasAudibleAudioSignal() directly.
function validateWhenSurfaceUnknown(
  videoTrack: MediaStreamTrack,
  stream: MediaStream
): InterviewScreenShareValidation {
  if (isLikelyCameraTrack(videoTrack)) {
    return { ok: false, issue: 'not_entire_screen' }
  }
  if (!hasLiveVideo(videoTrack)) {
    return { ok: false, issue: 'not_entire_screen' }
  }
  if (!hasLiveUnmutedAudio(stream)) {
    return { ok: false, issue: 'no_system_audio' }
  }
  return { ok: true }
}

export async function validateInterviewScreenShareAsync(
  stream: MediaStream
): Promise<InterviewScreenShareValidation> {
  const videoTrack = stream.getVideoTracks()[0]
  if (!videoTrack) {
    return { ok: false, issue: 'not_entire_screen' }
  }

  const surface = await waitForVideoDisplaySurface(videoTrack)

  if (surface === 'browser' || surface === 'window') {
    return { ok: false, issue: 'not_entire_screen' }
  }

  if (surface === 'monitor') {
    if (!hasLiveUnmutedAudio(stream)) {
      return { ok: false, issue: 'no_system_audio' }
    }
    return { ok: true }
  }

  if (surface != null && surface !== 'monitor') {
    return { ok: false, issue: 'unknown_surface' }
  }

  const expectsSurfaceInApi = reportsDisplaySurfaceCapability(videoTrack)

  if (expectsSurfaceInApi && !surface) {
    return { ok: false, issue: 'unknown_surface' }
  }

  return validateWhenSurfaceUnknown(videoTrack, stream)
}

export function stopMediaStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop())
}
